'use client';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useMapsLibrary} from '@vis.gl/react-google-maps';
import _ from 'lodash';

export type RemovedMarker = {
    routeId: string;
    pointId: string;
};

export type RouteProps = {
    color?: string;
    coordinates: google.maps.LatLngLiteral[];
    routeId: string;
    directionsRenderers: React.MutableRefObject<google.maps.DirectionsRenderer | {}>;
    map: google.maps.Map | null;
    isShowStartEnd: boolean;
    startMarker: google.maps.LatLngLiteral | null;
    endMarker: google.maps.LatLngLiteral | null;
};

export const Route = ({
    coordinates,
    color = '#2bd622',
    routeId,
    directionsRenderers,
    map,
    isShowStartEnd,
    startMarker,
    endMarker
}: RouteProps) => {
    const routersLibrary = useMapsLibrary('routes');

    const [directionsService, setDirectionsService] =
        useState<google.maps.DirectionsService | null>(null);

    const abortControllerRef = useRef<AbortController>(null);

    const [waypointsCache, setWaypointsCache] = useState<google.maps.LatLngLiteral[]>();
    const [directionsResultCache, setDirectionsResultCache] = useState<{
        result: google.maps.DirectionsResult;
        cachedWaypoints: google.maps.LatLngLiteral[];
    }>(undefined);

    useEffect(() => {
        return () => {
            directionsRenderers.current[routeId]?.setMap(null);
            delete directionsRenderers.current[routeId];
        };
    }, []);

    useEffect(() => {
        if (!routersLibrary) {
            return;
        }
        if (!directionsService) {
            setDirectionsService(new routersLibrary.DirectionsService());
        }
    }, [routersLibrary]);

    const waypoints = useMemo(
        () =>
            [isShowStartEnd ? startMarker : null, ...coordinates, isShowStartEnd ? endMarker : null]
                .filter(Boolean)
                .filter(marker => marker.lat && marker.lng),
        [isShowStartEnd, startMarker, endMarker, coordinates]
    );

    useEffect(() => {
        const newWaypointsCache = waypoints.map(({lat, lng}) => ({lat, lng}));

        if (!_.isEqual(newWaypointsCache, waypointsCache)) {
            setWaypointsCache(newWaypointsCache);
        }
    }, [waypoints]);

    const directionsResult: Promise<google.maps.DirectionsResult | undefined> =
        useMemo(async () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            abortControllerRef.current = new AbortController();
            return new Promise((resolve, reject) => {
                const abortListener = () => {
                    abortControllerRef.current.signal.removeEventListener('abort', abortListener);
                    reject();
                };
                abortControllerRef.current.signal.addEventListener('abort', abortListener);

                if (waypointsCache[0] && directionsService) {
                    if (_.isEqual(directionsResultCache?.cachedWaypoints, waypointsCache)) {
                        resolve(directionsResultCache.result);
                    } else {
                        directionsService.route(
                            {
                                origin: waypointsCache[0],
                                destination: waypointsCache[waypoints.length - 1],
                                travelMode: google.maps.TravelMode.DRIVING,
                                provideRouteAlternatives: false,
                                optimizeWaypoints: true,
                                waypoints: waypointsCache
                                    .map(location => ({location, stopover: true}))
                                    .slice(1, -1)
                            },
                            (result, status) => {
                                if (status !== google.maps.DirectionsStatus.OK) {
                                    console.error('Directions request failed due to ' + status);
                                    reject();
                                } else {
                                    setDirectionsResultCache({
                                        result,
                                        cachedWaypoints: waypointsCache
                                    });
                                    resolve(result);
                                }
                            }
                        );
                    }
                } else {
                    resolve(undefined);
                }
            })
                .then(x => x as google.maps.DirectionsResult)
                .catch(() => undefined);
        }, [waypointsCache, directionsService]);

    const handleRouteRender = useCallback(async () => {
        const result = await directionsResult;

        directionsRenderers.current[routeId]?.setMap(null);
        delete directionsRenderers.current[routeId];

        if (result) {
            const directionsRenderer = new routersLibrary.DirectionsRenderer({map});

            directionsRenderer.setDirections(result);
            directionsRenderer.setOptions({
                suppressMarkers: true,
                polylineOptions: {
                    map,
                    strokeColor: color,
                    strokeOpacity: 0.7,
                    strokeWeight: 3
                },
                preserveViewport: true
            });
            directionsRenderers.current[routeId] = directionsRenderer;
        }
    }, [directionsResult, map, color]);

    useEffect(() => {
        setTimeout(() => handleRouteRender(), 100);
    }, [handleRouteRender, map.getZoom()]);

    useEffect(() => {
        handleRouteRender();
    }, [color]);

    return null;
};
